<script lang="ts">
  export interface ButtonProps {
    /**
     * Renders the secondary variant
     */
    secondary?: boolean;

    /**
     * Renders the button as a container for an Icon
     */
    icon?: boolean;

    /**
     * Renders the button as a container for an IconLabel
     */
    iconLabel?: boolean;

    /**
     * Renders the tertiary variant
     */
    tertiary?: boolean;

    /**
     * Renders the component inline
     */
    inline?: boolean;

    /**
     * Renders a color variant
     */
    color?: 'blue' | 'red';

    /**
     * The `to` prop for vue-router's `RouterLink` component. If defined, the button will render as a `<router-link />`.
     */
    to?: RouteLocationRaw;

    /**
     * Button link. If defined, the button will render as an `<a />` tag.
     */
    href?: string;

    /**
     * Displays a spinning loading icon in place of the button text and disables the button
     */
    isLoading?: boolean;

    /**
     * Disables the button
     */
    disabled?: boolean;
  }
</script>

<script lang="ts" setup>
  import { computed, useCssModule } from 'vue';
  import type { RouteLocationRaw } from 'vue-router';

  import Icon from '../Icon/Icon.vue';

  defineOptions({
    name: 'll-button',
  });

  const props = withDefaults(defineProps<ButtonProps>(), {
    secondary: false,
    icon: false,
    iconLabel: false,
    tertiary: false,
    inline: false,
    color: undefined,
    href: '',
    to: undefined,
    isLoading: false,
    disabled: undefined,
  });
  const classes = useCssModule();

  enum ButtonTypes {
    Icon = 'icon',
    IconLabel = 'iconLabel',
    Secondary = 'secondary',
    Tertiary = 'tertiary',
    Inline = 'inline',
    Primary = 'primary',
  }

  type ButtonType = `${ButtonTypes}`;

  const buttonType = computed<ButtonType>(() => {
    for (const type of Object.values(ButtonTypes)) {
      if (props[type]) {
        return type;
      }
    }

    return ButtonTypes.Primary;
  });

  const is = computed(() => {
    if (props.to) {
      return 'router-link';
    }

    if (props.href) {
      return 'a';
    }

    return 'button';
  });

  const buttonAttrs = computed(() => {
    if (props.to) {
      return {
        to: props.to,
      };
    }

    if (props.href) {
      return {
        href: props.href,
      };
    }

    return {};
  });
</script>

<template>
  <component
    :is="is"
    :disabled="props.isLoading || props.disabled ? true : undefined"
    v-bind="buttonAttrs"
    class="stash-button"
    :class="[
      classes.button,
      `stash-button--${buttonType}`,
      classes[`button--${buttonType}`],
      {
        [`stash-button--${props.color}`]: props.color,
        [classes[`button--${props.color}`]]: props.color,
        'stash-button--is-loading': props.isLoading,
      },
    ]"
    data-test="ll-button"
  >
    <Icon v-if="props.isLoading" name="loading-empty" class="animate-spin" />
    <!-- @slot default -->
    <slot v-else></slot>
  </component>
</template>

<style module>
  @reference "../../../styles/main.css";

  .button {
    border-radius: var(--radius-sm);
    border-width: 1px;
    cursor: pointer;
    display: inline-block;
    font-size: 0.875rem;
    font-weight: var(--stash-font-weight-semibold);
    line-height: 2.125rem;
    margin: 0;
    min-width: 144px;
    padding: 0 --spacing(3);
    text-align: center;
    text-decoration: none;
    vertical-align: middle;
    white-space: nowrap;
  }

  @media (width >= theme(--breakpoint-lg)) {
    /* `focus` and `hover` should be added only for large screens */
    .button:focus {
      box-shadow: 0 0 0 3px rgb(0 123 255 / 14%);
      outline: none;
    }

    .button:hover {
      text-decoration: none;
    }
  }

  .button[disabled] {
    cursor: default;
    pointer-events: none;
  }

  /* Solid treatment */
  .button--solid {
    background-color: var(--button-color);
    border-color: transparent;
    color: var(--text-color);
  }

  @media (width >= theme(--breakpoint-lg)) {
    /* `hover` should be added only for large screens */
    .button--solid:hover {
      background-color: var(--button-hover-color);
    }
  }

  .button--solid:active {
    background-color: var(--button-hover-color);
  }

  .button--solid[disabled]:not(:global(.stash-button--is-loading)) {
    --button-color: var(--color-ice-500) !important;
  }

  /* Ghost treatment */
  .button--ghost {
    background-color: rgb(0 0 0 / 0%);
    border-color: var(--button-color);
    color: var(--text-color);
  }

  @media (width >= theme(--breakpoint-lg)) {
    /* `hover` should be added only for large screens */
    .button--ghost:hover {
      background-color: var(--button-hover-color);
    }
  }

  .button--ghost:active {
    background-color: var(--button-hover-color);
  }

  .button--ghost[disabled]:not(:global(.stash-button--is-loading)) {
    --button-color: var(--color-ice-500) !important;
    --text-color: var(--color-ice-500) !important;
  }

  /* Types */
  .button--primary {
    composes: button--solid;
    --button-color: var(--color-blue-500);
    --button-hover-color: var(--color-blue-hover);
    --text-color: var(--color-white);
  }

  .button--secondary {
    composes: button--ghost;
    --button-color: var(--color-ice-500);
    --button-hover-color: var(--color-ice-700-hover);
    --text-color: var(--color-ice-700);
  }

  .button--tertiary {
    composes: button--ghost;
    --button-color: var(--color-white);
    --button-hover-color: var(--color-white-hover);
    --text-color: var(--color-white);
  }

  .button--icon,
  .button--iconLabel {
    /* https://developers.google.com/web/fundamentals/accessibility/accessible-styles */
    border: 0;
    border-radius: var(--radius-sm);
    height: --spacing(12);
    min-width: --spacing(12);
    text-decoration: none;
  }

  .button--icon {
    padding: --spacing(3);
    width: --spacing(12);
  }

  .button--iconLabel {
    padding: 0;
    width: unset;
  }

  @media (width >= theme(--breakpoint-lg)) {
    /* `hover` should be added only for large screens */
    .button--icon:hover,
    .button--iconLabel:hover {
      text-decoration: none;
    }
  }

  .button--icon svg,
  .button--iconLabel svg {
    display: block;
    margin-left: auto;
    margin-right: auto;
  }

  .button--icon[disabled]:not(:global(.stash-button--is-loading)) svg,
  .button--iconLabel[disabled]:not(:global(.stash-button--is-loading)),
  .button--iconLabel[disabled]:not(:global(.stash-button--is-loading)) svg {
    color: var(--color-ice-500);
  }

  .button--inline {
    border: 0;
    color: var(--color-blue-500);
    font-weight: var(--stash-font-weight-medium);
    line-height: 1.5;
    min-width: 0;
    padding: 0;
  }

  .button--inline:hover {
    text-decoration: underline;
  }

  .button--inline[disabled] {
    color: var(--color-ice-500);
  }

  /* Colors */
  .button--blue.button--primary,
  .button--blue.button--secondary {
    --button-color: var(--color-blue-500);
  }

  .button--blue.button--primary {
    --button-hover-color: var(--color-blue-hover);
  }

  .button--blue.button--secondary {
    --text-color: var(--color-blue-500);
    --button-hover-color: var(--button-secondary-blue-hover);
  }

  .button--red.button--primary,
  .button--red.button--secondary {
    --button-color: var(--color-red-500);
  }

  .button--red.button--primary {
    --button-hover-color: var(--color-red-hover);
  }

  .button--red.button--secondary {
    --text-color: var(--color-red-500);
    --button-hover-color: var(--button-secondary-red-hover);
  }

  /**
 * Button Grid
 * TODO: Move into separate component
 * https://leaflink.atlassian.net/browse/STASH-230
 * See `styles/elements/_buttons.scss` for button-grid (parent element) specific styles.
 */
  :global(.button-grid) > .button,
  :global(.header-button-grid) > .button {
    width: 50%;
  }

  :global(.button-grid) > .button + .button {
    margin-left: --spacing(6);
  }

  @media (width >= theme(--breakpoint-md)) {
    :global(.button-grid) > .button {
      width: auto;
    }
  }

  :global(.header-button-grid) > .button + .button {
    margin-left: --spacing(6);
  }

  @media (width >= theme(--breakpoint-lg)) {
    :global(.header-button-grid) > .button {
      width: auto;
    }
  }
</style>
